/**
 * #2108 — CallSteward Support: the pinned conversation, and whether it is live.
 *
 * Ed, 2026-10-21: "CallSteward Support should be a default channel" and "the cs
 * support should exist at the bottom of the chat box and always be visible."
 *
 * ══ WHY THIS IS A MODULE AND NOT COMPONENT CODE ══
 *
 * The same reason as `chatHours.js`, `chatPeople.js` and `chatAttachments.js`:
 * `chatPanel.vue` cannot be loaded under node (Vue, Vuex, the Firebase SDK), and
 * every mistake available here is a QUIET one — a support conversation listed
 * among the client's channels, a ticket form offered when no ticket can be
 * created, or an office-hours lock applied to the one conversation Ed's rules
 * say it must never apply to. Rules go where they can be tested.
 *
 * The server half is cs3-chat #27 (!16): `GET /api/me/channels` returns the
 * `support` channel, `GET /api/support/availability` says whether live chat is
 * on offer, and `POST /api/support/ticket` creates a FreeScout conversation.
 */

/** cs3-chat `ChannelKind::Support`. */
export const SUPPORT_KIND = 'support';

/**
 * The group key the pinned row gets, and it is deliberately one NO HEADING USES.
 *
 * `chatPanel`'s `groupsShown` builds its headings from `GROUPS` plus
 * `direct` and keeps only the rows whose `grp` matches one of them — so giving
 * the support conversation a group of its own is what takes it out of the
 * scrolling list ("It doesn't appear among the other channels", issue item 1)
 * without a second filter anywhere. The pinned row below the list renders it.
 */
export const SUPPORT_GROUP = 'support';

/**
 * What the row says.
 *
 * ⚠ NOT THE SERVER'S `name`, DELIBERATELY. cs3-chat stores "CallSteward
 * Support" (`SupportChannels::NAME`) and the two agree today — but this row is
 * a fixed part of the CS3 panel's furniture, not a channel someone named, and
 * a rename on the server must not turn the pinned entry into something a user
 * does not recognise. The id is what addresses it; this is what a person reads.
 */
export const SUPPORT_LABEL = 'CallSteward Support';

/** Is this the support conversation? */
export function isSupport(channel) {
    return !!channel && channel.kind === SUPPORT_KIND;
}

/**
 * Give the support conversation its panel identity.
 *
 * The counterpart of `labelDirect` (#2098) and applied in the same place, to
 * every channel in the roster as it arrives.
 */
export function labelSupport(channel) {
    const c = channel || {};
    if (c.kind !== SUPPORT_KIND) return c;

    c.grp = SUPPORT_GROUP;
    c.label = SUPPORT_LABEL;
    c.name = SUPPORT_LABEL;

    return c;
}

/**
 * The support conversation out of a roster, or null.
 *
 * ⚠ THE FIRST ONE, AND THERE CAN ONLY BE ONE. cs3-chat derives the id from the
 * owner's uid (`c<cid>_support_<uid>`), so a roster holds at most one for the
 * person reading it — a staff roster holds everyone's, which is why CS staff get
 * no pinned row at all (`pinnedSupport` below).
 */
export function supportChannel(channels, meUid) {
    const list = Array.isArray(channels) ? channels : [];

    return list.find((c) => isSupport(c) && supportOwner(c) === meUid) || null;
}

/**
 * WHOSE support conversation this is.
 *
 * cs3-chat `Channel::supportKey` — the key is the owner's uid, which is the
 * only thing on a roster row that says whose conversation it is. That matters
 * for CS staff, whose roster contains every support conversation in the silo:
 * without this a support agent's panel would pin somebody else's.
 */
export function supportOwner(channel) {
    const c = channel || {};
    if (c.kind !== SUPPORT_KIND) return null;

    return typeof c.key === 'string' && c.key !== '' ? c.key : null;
}

/**
 * The row to pin at the bottom of the list, or null when there is none.
 *
 * Null for CS staff (they have no support conversation of their own — cs3-chat
 * `SupportChannels::ensure` returns null for them) and null before the roster
 * has arrived. The panel draws nothing rather than a placeholder: a pinned
 * "CallSteward Support" that does not open yet is worse than one that appears a
 * moment later with the rest of the list.
 */
export function pinnedSupport(channels, meUid, isStaff) {
    if (isStaff) return null;

    return supportChannel(channels, meUid);
}

/*
 * ── availability ────────────────────────────────────────────────────────────
 *
 * cs3-chat answers `{live, reason, message, next_open?}` where reason is one of
 * `open`, `outside_hours`, `no_agent_online`, `not_configured`.
 */

/** Live chat is on offer. */
export const OPEN = 'open';
/** FreeScout says the desk is shut. */
export const OUTSIDE_HOURS = 'outside_hours';
/** Inside hours, nobody at the desk. */
export const NO_AGENT = 'no_agent_online';
/** No FreeScout on this deployment — see `offersTicket`. */
export const NOT_CONFIGURED = 'not_configured';
/** The panel could not ask. Never sent by the server; see `availabilityFailed`. */
export const UNKNOWN = 'unknown';

const REASONS = [OPEN, OUTSIDE_HOURS, NO_AGENT, NOT_CONFIGURED];

/**
 * Shape the availability response.
 *
 * ⚠ AN UNRECOGNISED `reason` IS NOT TRUSTED TO BE LIVE. A newer server could
 * add one; `live` is taken from the server (it is the server's decision) but the
 * reason falls back to `unknown`, so the panel's own branching never has to
 * guess what a word it has never seen means.
 */
export function normaliseAvailability(raw) {
    const r = raw || {};
    const reason = REASONS.indexOf(r.reason) >= 0 ? r.reason : UNKNOWN;

    return {
        live: r.live === true,
        reason,
        message: typeof r.message === 'string' && r.message !== '' ? r.message : reasonText(reason),
        nextOpen: typeof r.next_open === 'string' && r.next_open !== '' ? r.next_open : null,
    };
}

/**
 * What the panel assumes when it could not ask at all (a network failure, an
 * expired session, a 500).
 *
 * ⚠ FAILS **OPEN**, AND THAT IS THE OPPOSITE OF THE SERVER'S RULE — on purpose.
 * cs3-chat's `AgentPresence` fails closed because it is deciding whether to
 * promise someone a live agent. Here the question is different: what does the
 * person get to DO right now. A support message is never lost — it is an
 * ordinary message in an ordinary channel that any CS Admin or CS Support can
 * read whenever they next look — whereas a ticket form shown because our own
 * request failed would be a dead end if the same outage also breaks
 * `POST /api/support/ticket`. So an unknown answer leaves the conversation
 * usable and says that it could not be checked.
 */
export function availabilityFailed() {
    return { live: true, reason: UNKNOWN, message: reasonText(UNKNOWN), nextOpen: null };
}

/**
 * Should the panel offer the ticket form instead of the composer?
 *
 * ⚠ `not_configured` DOES NOT OFFER A TICKET, and that is the state stage- is
 * in until host-pm sets `CHAT_FREESCOUT_*`. With no FreeScout there is nothing
 * to create a ticket IN — `POST /api/support/ticket` answers 503 — so offering
 * the form would be a button that cannot work. The conversation still reaches
 * CallSteward, which is the honest route, so that is the one the panel keeps.
 *
 * `unknown` does not offer one either, for the reason in `availabilityFailed`.
 */
export function offersTicket(availability) {
    const a = availability || {};

    return a.live !== true && (a.reason === OUTSIDE_HOURS || a.reason === NO_AGENT);
}

/**
 * When support reopens, as a DATE the panel can format.
 *
 * ⚠ PARSED HERE, NOT IN THE TEMPLATE. FreeScout's module sends DATE_ATOM
 * (`2026-10-23T09:00:00-04:00`), and the panel's `shortWhen` takes a Date and
 * calls `getDay()` on it — handing it the raw string threw inside the render,
 * which in Vue 2 blanks the component with only a console line to say why.
 * Caught by rendering the real panel for #2108's screenshots; now it is parsed
 * where the failure can be a null instead of an exception.
 *
 * Null when there is nothing to show or the value will not parse, and the strip
 * that prints it is hidden on null rather than printing "Invalid Date".
 */
export function nextOpenDate(availability) {
    const raw = (availability || {}).nextOpen;
    if (typeof raw !== 'string' || raw === '') return null;

    const at = new Date(raw);

    return Number.isNaN(at.getTime()) ? null : at;
}

/** The sentence beside the state, when the server did not send its own. */
export function reasonText(reason) {
    return {
        [OPEN]: 'CallSteward Support is online.',
        [OUTSIDE_HOURS]: 'CallSteward Support is closed right now.',
        [NO_AGENT]: 'No one is at the CallSteward Support desk right now.',
        [NOT_CONFIGURED]: 'Support chat is not set up on this environment yet.',
        [UNKNOWN]: 'We could not check whether CallSteward Support is online.',
    }[reason] || '';
}

/**
 * The second line: what the person can do about it.
 *
 * Separate from `reasonText` because the two change independently — the reason
 * is the server's fact and this is the panel's offer — and because the ticket
 * sentence must not appear under a reason that has no ticket behind it.
 */
export function offerText(availability) {
    const a = availability || {};
    if (offersTicket(a)) {
        return 'Leave a ticket and the support team will reply by email.';
    }
    if (a.reason === NOT_CONFIGURED) {
        return 'You can still send a message here; the support team will see it.';
    }
    if (a.reason === UNKNOWN) {
        return 'You can still send a message here.';
    }

    return '';
}

/**
 * `GET /api/support/availability` and `POST /api/support/ticket` failures, as
 * codes the panel has a sentence for.
 *
 * 503 is the one that is not a fault: cs3-chat answers it when
 * `CHAT_FREESCOUT_*` is unset, which is the shipping state until host-pm stores
 * the secrets on Ed's yes.
 */
export function ticketFailureCode(status) {
    return {
        401: 'chat_session_expired',
        422: 'ticket_rejected',
        429: 'ticket_too_many',
        502: 'support_unreachable',
        503: 'support_not_configured',
    }[status] || 'chat_unreachable';
}

/**
 * ⚠ 422 HAS TWO MEANINGS AND THE PANEL MUST TELL THEM APART.
 *
 * cs3-chat refuses a ticket with 422 both when the message is empty or too long
 * AND when it has no email address on file for this person (`errors.email`) —
 * and only the second one is the user's own standing problem rather than
 * something they can fix by rewording. Issue item 4: "have the ticket form say
 * they need one."
 *
 * @param {object} body  the 422 response body, `{error, message, errors}`
 */
export function isEmailRefusal(body) {
    const errors = (body || {}).errors || {};

    return Object.prototype.hasOwnProperty.call(errors, 'email');
}

/** What the form says for each failure. */
export function ticketErrorText(code) {
    return {
        ticket_no_email: 'We do not have an email address for you, so a ticket cannot be opened — '
            + 'a reply would have nowhere to go. Ask your Client Admin to add one to your profile, '
            + 'or send a message here instead.',
        ticket_rejected: 'That message could not be sent. Check the text and try again.',
        ticket_too_many: 'That is several tickets in a short time. Please wait a little before '
            + 'opening another.',
        support_unreachable: 'We could not reach the support desk just now. Please try again '
            + 'shortly, or send a message here instead.',
        support_not_configured: 'Tickets are not available on this environment yet. Send a message '
            + 'here instead — the support team will see it.',
        chat_session_expired: 'Your chat session has expired. Reload the page and try again.',
        chat_unreachable: 'We could not reach support just now. Please try again shortly.',
    }[code] || 'We could not open a ticket just now. Please try again shortly.';
}

/**
 * The ticket body, checked before a round trip.
 *
 * The same 4,000-character ceiling a chat message has, because cs3-chat runs the
 * ticket through the very same `MessageSanitizer` — a ticket refused by the
 * server for length after the person wrote it is a worse outcome than a counter
 * that stops them.
 */
export const MAX_TICKET_CHARS = 4000;

export function ticketReady(text) {
    const body = String(text == null ? '' : text).trim();

    return body !== '' && body.length <= MAX_TICKET_CHARS;
}
